import { MarketPeriodInterface } from 'features/dispatcher/dispatcherTypes';
import moment from 'moment';
import momentTimezone from 'moment-timezone';
import { TFunction } from 'i18next';

export type MarketDayInterval = {
  marketDayStart: string;
  marketDayEnd: string;
};

/**
 * FE calculated,
 * start of day is midnight in slovak timezone CET/CEST UTC+1/UTC+2
 * FOR TESTING PURPOSES ONLY - DO NOT USE IN REAL IMPLEMENTATION!!!
 * USE getMarketDayIntervalFromMarketPeriods INSTEAD
 */
export const getMarketDayInterval = (
  date: string | Date
): MarketDayInterval => {
  const marketDayStart = momentTimezone(date)
    .tz('Europe/Bratislava')
    .startOf('day'); // start of day in provided timezone, if no timezone is set uses local timezone
  const marketDayEnd = marketDayStart.clone().add(1, 'day').startOf('day');

  return {
    marketDayStart: marketDayStart.toISOString(),
    marketDayEnd: marketDayEnd.toISOString()
  };
};

// ---------------

export const getMarketDayIntervalFromMarketPeriods = (
  marketPeriods: MarketPeriodInterface[]
): MarketDayInterval | undefined => {
  const firstPeriod = marketPeriods.at(0);
  const lastPeriod = marketPeriods.at(-1);
  if (!firstPeriod || !lastPeriod) {
    return undefined;
  }
  return {
    marketDayStart: firstPeriod.startDateTime,
    marketDayEnd: lastPeriod.endDateTime
  };
};

export const isTimestampInMarketPeriod = (
  timestamp: number | string | Date,
  interval: MarketPeriodInterface,
  inclusivity?: '()' | '[)' | '(]' | '[]'
): boolean => {
  const timestampMoment = moment(timestamp);
  return timestampMoment.isBetween(
    interval.startDateTime,
    interval.endDateTime,
    undefined,
    inclusivity ?? '[)'
  );
};

export const findMarketPeriodByTimestamp = (
  timestamp: string | Date,
  marketPeriods: MarketPeriodInterface[],
  inclusivity?: '()' | '[)' | '(]' | '[]'
): MarketPeriodInterface | undefined => {
  const timestampMoment = moment(timestamp);
  return marketPeriods.find((period) => {
    return timestampMoment.isBetween(
      period.startDateTime,
      period.endDateTime,
      undefined,
      inclusivity ?? '[)'
    );
  });
};

export const getCurrentMarketPeriod = (
  marketPeriods: MarketPeriodInterface[]
): MarketPeriodInterface | undefined => {
  return findMarketPeriodByTimestamp(new Date(), marketPeriods);
};

export const getTimeLabelTextForMarketPeriod = (
  marketPeriod: MarketPeriodInterface,
  t: TFunction,
  componentRoot: string
): string => {
  const periodText = t(`${componentRoot}.periodText`);
  return `${marketPeriod.timeUnitNumber}. ${periodText} (${marketPeriod.timeFrom} - ${marketPeriod.timeTo})`;
};

export const getMarketPeriodByUnixTimestamp = (
  timestampMs: number,
  marketPeriods: MarketPeriodInterface[]
): MarketPeriodInterface | undefined => {
  const dateTime = new Date(timestampMs);
  return findMarketPeriodByTimestamp(dateTime, marketPeriods);
};

export const sortMarketPeriodsByTimeFromAsc = (
  marketPeriods: MarketPeriodInterface[]
) => {
  return [...marketPeriods].sort((a, b) => (a.timeFrom < b.timeFrom ? -1 : 1));
};
